import { useState } from 'react'
import { fDate } from '@/utils/format-date'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EyeIcon, MoreHorizontal, Settings } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import OrderDetailDialog from './order-detail-dialog'
import OrderPrivilegeEditDialog from './order-privilege-edit-dialog'
import OrderShippingStatusDialog from './order-shipping-status-dialog'
import {
  type OrderPrivilege,
  OrderPrivilegeStatus,
  ShippingStatus,
  PaymentStatus,
} from './order-privilege.types'
import {
  getOrderStatusLabel,
  getPaymentStatusLabel,
  getPaymentTypeLabel,
  getShippingStatusLabel,
  getOrderStatusBadgeClass,
  getShippingStatusBadgeClass,
  getPaymentStatusBadgeClass,
} from './helper'

interface OrderPrivilegeTableProps {
  orders: OrderPrivilege[]
  loading: boolean
  onOrderUpdated?: () => void
}

export default function OrderPrivilegeTable({
  orders,
  loading,
  onOrderUpdated,
}: OrderPrivilegeTableProps) {
  const [detailOpen, setDetailOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderPrivilege | null>(
    null,
  )

  const openDetail = (order: OrderPrivilege) => {
    setSelectedOrder(order)
    setDetailOpen(true)
  }

  const openStatus = (order: OrderPrivilege) => {
    setSelectedOrder(order)
    setStatusOpen(true)
  }

  // const openEdit = (order: OrderPrivilege) => {
  //   setSelectedOrder(order)
  //   setEditOpen(true)
  // }

  const renderStatusBadge = (status: OrderPrivilegeStatus) => {
    const className = getOrderStatusBadgeClass(status)
    return <Badge className={className}>{getOrderStatusLabel(status)}</Badge>
  }

  const renderShippingBadge = (status: ShippingStatus) => {
    const className = getShippingStatusBadgeClass(status)
    return <Badge className={className}>{getShippingStatusLabel(status)}</Badge>
  }

  const renderPaymentBadge = (status: PaymentStatus) => {
    const className = getPaymentStatusBadgeClass(status)
    return <Badge className={className}>{getPaymentStatusLabel(status)}</Badge>
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="h-12 w-32 bg-muted rounded animate-pulse" />
                <div className="h-8 w-20 bg-muted rounded animate-pulse" />
                <div className="h-8 w-24 bg-muted rounded animate-pulse" />
                <div className="h-8 w-24 bg-muted rounded animate-pulse" />
                <div className="h-8 w-24 bg-muted rounded animate-pulse" />
                <div className="h-8 w-24 bg-muted rounded animate-pulse" />
                <div className="h-8 w-28 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="space-y-2">
            <h3 className="text-lg font-medium">ไม่พบคำสั่งซื้อรางวัล</h3>
            <p className="text-sm text-muted-foreground">
              ยังไม่มีคำสั่งซื้อรางวัล หรือไม่พบข้อมูลตามเงื่อนไขที่ค้นหา
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>เลขที่คำสั่งซื้อ</TableHead>
                <TableHead className="text-center">สถานะคำสั่งซื้อ</TableHead>
                <TableHead className="text-center">สถานะการชำระเงิน</TableHead>
                <TableHead className="text-center">สถานะการจัดส่ง</TableHead>
                <TableHead className="text-center">ประเภทการชำระเงิน</TableHead>
                <TableHead className="text-center">เลขพัสดุ</TableHead>
                <TableHead className="text-center">วันที่สร้าง</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <div className="font-medium">{order.orderNumber}</div>
                  </TableCell>
                  <TableCell className="text-center">
                    {renderStatusBadge(order.status)}
                  </TableCell>
                  <TableCell className="text-center">
                    {renderPaymentBadge(order.paymentStatus)}
                  </TableCell>
                  <TableCell className="text-center">
                    {renderShippingBadge(order.shippingStatus)}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-sm text-muted-foreground">
                      {getPaymentTypeLabel(order.paymentType)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-sm">
                      {order.trackingNumber || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground">
                    {fDate(order.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openDetail(order)}>
                          <EyeIcon className="h-4 w-4" />
                          รายละเอียด
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openStatus(order)}>
                          <Settings className="h-4 w-4" />
                          สถานะการจัดส่ง
                        </DropdownMenuItem>
                        {/* <DropdownMenuItem onClick={() => openEdit(order)}>
                          <Pencil className="h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem> */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <OrderDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        order={selectedOrder}
      />

      <OrderShippingStatusDialog
        open={statusOpen}
        onOpenChange={setStatusOpen}
        order={selectedOrder}
        onSaved={onOrderUpdated}
      />

      <OrderPrivilegeEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        order={selectedOrder}
        onSaved={onOrderUpdated}
      />
    </>
  )
}
